<template>
  <section>
    <LoadingIndicator v-if="submitting" />
    <ValidationObserver slim v-slot="{ invalid }">
      <b-form @submit.prevent="verify" novalidate>
        <h2>{{ $t('login.forgot-password-label') }}</h2>
        <p>{{ $t('login.forgot-password-description') }}</p>

        <EwnFormControl vid="username" label-for="username" :label="$t('login.username-label')"
                        v-slot="{ state }" :rules="{ required: true }">
          <b-form-input id="username" name="username" :state="state"
            v-model="usernameInternal" autofocus autocapitalize="off"
            autocomplete="username" autocorrect="off" spellcheck="false" />
        </EwnFormControl>

        <div class="invalid-feedback" v-if="errorMessageInternal">{{ errorMessageInternal }}</div>

        <div class="main-button-container">
          <button class="primary" :disabled="invalid || submitting">
            {{ $t('login.request-password-reset-button') }}
          </button>
          <div class="link-container">
            <button type="button" class="link" @click="returnToLogin">
              {{ $t('login.return-button') }}
            </button>
            <router-link :to="{ path: '/contact-us', query: { 'brand_template': brandTemplate }}"
                        class="button-link">
              {{ $t('login.contact-button') }}
            </router-link>
          </div>
        </div>

        <!-- This is at the bottom to be last in the focus order -->
        <vue-recaptcha
          ref="recaptcha"
          @verify="request"
          @expired="reset"
          size="invisible"
          badge="bottomright"
          :sitekey="siteKey">
        </vue-recaptcha>
      </b-form>
    </ValidationObserver>
  </section>
</template>

<script>
  import { noop } from '@ewn/fp';
  import { startTimeout } from '@ewn/time';
  import { computed, defineComponent, onUnmounted, ref } from 'vue';
  import { BForm, BFormInput } from 'bootstrap-vue';
  import { ValidationObserver } from 'vee-validate';
  import VueRecaptcha from 'vue-recaptcha';
  import { useForgotPassword } from './useForgotPassword';
  import { useI18n } from '@/i18n';
  import EwnFormControl from '@/modules/forms/components/EwnFormControl';
  import LoadingIndicator from '@/modules/loading/components/LoadingIndicator';
  import { cleanupRecaptcha } from '@/modules/recaptcha';
  import { useRouter } from '@/router';
  import { reCaptcha as config } from '@/runtime-config';

  const recaptchaTimeoutMilliseconds = 1000 * 30;

  export default defineComponent({
    name: 'ForgotPassword',
    components: {
      'b-form': BForm,
      'b-form-input': BFormInput,
      EwnFormControl,
      LoadingIndicator,
      ValidationObserver,
      'vue-recaptcha': VueRecaptcha
    },
    props: {
      username: {
        type: String
      },
      errorMessage: {
        type: String
      }
    },
    setup (props) {
      const recaptcha = ref();
      const fromLogin = ref(true);
      const submitting = ref(false);
      const errorMessageInternal = ref(props.errorMessage);
      const usernameInternal = ref(props.username);
      const i18n = useI18n();
      const router = useRouter();
      const brandTemplate = computed(() => router.currentRoute.query.brand_template);

      const { requestReset } = useForgotPassword();

      onUnmounted(() => cleanupRecaptcha());

      function reset () {
        // Reset could occur after unmount but we don't care at that point
        if (recaptcha.value) {
          recaptcha.value.reset();
        }
        submitting.value = false;
      }
      let stopTimeout = noop;
      return {
        errorMessageInternal,
        fromLogin,
        brandTemplate,
        recaptcha,
        async request (reCaptchaResponse) {
          stopTimeout();
          errorMessageInternal.value = null;
          errorMessageInternal.value = await requestReset({
            username: usernameInternal.value,
            reCaptchaResponse
          });
          reset();
        },
        reset,
        returnToLogin () {
          if (fromLogin.value) {
            router.back();
          } else {
            // User entered through a different entry point (e.g. password reset link)
            // so we don't have any params to preserve
            router.replace({ path: '/login', query: { brand_template: this.brandTemplate } });
          }
        },
        siteKey: config.siteKey,
        verify () {
          submitting.value = true;
          // execute will hang on a dropped internet connection
          // so we have to set a reasonable limit and reset manually
          stopTimeout = startTimeout(() => {
            reset();
            errorMessageInternal.value = i18n.t('login.recaptcha-error');
          }, recaptchaTimeoutMilliseconds);
          recaptcha.value.execute();
        },
        submitting,
        usernameInternal
      };
    },
    beforeRouteEnter (_, from, next) {
      next(vm => {
        vm.fromLogin = from.path === '/login';
      });
    }
  });
</script>

<style lang="scss" scoped>
  section {
    max-width: 400px;
  }

  form {
    width: 100%;
  }

  h2 {
    text-align: center;
  }

  h2,
  p {
    margin-bottom: 16px;
  }
</style>
